import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { Badge, Button, Row, errorToast, spacing, toast } from '@/components/ui'
import { $api, $client } from '@/lib/fetch'
import { safeHref } from '@/lib/helpers'
import { catOf } from '@/lib/types'

const AUTOFILL_TOOLTIP =
  "Cette entité attend un autofill : le scraper Instagram n'a pas encore récupéré sa bio, son lien externe ou sa photo. Elle est masquée du site public tant que l'autofill n'a pas réussi."

export default function AdminEntitiesCRUD() {
  const navigate = useNavigate()
  const { data, refetch, isLoading } = $api.useQuery('get', '/api/entities')
  const { data: kindsData } = $api.useQuery('get', '/api/entity-kinds')

  const deleteEntity = async (id: number, name: string) => {
    if (!confirm(`Supprimer "${name}" ?`)) return
    const resp = await $client.DELETE('/api/entities/{id}', {
      params: { path: { id } },
    })
    if (resp.error) errorToast('Erreur à la suppression', resp.error)
    else {
      toast({ color: 'success', title: 'Entité supprimée' })
      refetch()
    }
  }

  const kindName = (id: number) =>
    kindsData?.kinds?.find((k: { id: number }) => k.id === id)?.name ??
    String(id)

  if (isLoading) return <p style={{ color: 'var(--ink-2)' }}>Chargement…</p>

  return (
    <>
      <Row justify="flex-end" style={{ marginBottom: spacing.lg }}>
        <Button
          variant="primary"
          onClick={() => navigate({ to: '/admin/entities/new' })}
        >
          + Nouvelle entité
        </Button>
      </Row>
      <div className="oumma-table-wrap">
        <table className="oumma-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Type</th>
              <th>Tags</th>
              <th>Lieu</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.entities ?? []).map((e) => (
              <tr key={e.id}>
                <td>
                  <Row gap="xs" align="center">
                    <span style={{ fontWeight: 600 }}>{e.name}</span>
                    {e.requires_autofill && (
                      <span
                        title={AUTOFILL_TOOLTIP}
                        aria-label={AUTOFILL_TOOLTIP}
                        style={{
                          display: 'inline-flex',
                          color: 'var(--warning, #c97a00)',
                          cursor: 'help',
                        }}
                      >
                        <AlertTriangle size={14} />
                      </span>
                    )}
                  </Row>
                  {safeHref(e.link) && (
                    <a
                      href={safeHref(e.link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 12, color: 'var(--ink-2)' }}
                    >
                      ↗ lien
                    </a>
                  )}
                </td>
                <td>
                  <Badge tone="gold">{kindName(e.kind_id)}</Badge>
                </td>
                <td>
                  <Row gap="xs" style={{ flexWrap: 'wrap' }}>
                    {(e.tags ?? []).map((tag: string) => (
                      <Badge key={tag} tone={catOf(tag).tone}>
                        {tag}
                      </Badge>
                    ))}
                  </Row>
                </td>
                <td style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  {e.location_text ?? '—'}
                </td>
                <td>
                  <Row gap="sm">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        navigate({
                          to: '/admin/entities/$id/edit',
                          params: { id: String(e.id) },
                        })
                      }
                    >
                      Éditer
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => deleteEntity(e.id, e.name)}
                    >
                      Supprimer
                    </Button>
                  </Row>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
